import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../../OrbitContext';
import { TOrbit } from '../../i18n/TModules';
import { ResponsePanel } from '../../support/response/responsePrimitives';
import { responseType, rsColor } from '../../support/response/responseScale';
import { OrbitTurn } from '../../types';
import { useOrbitL4Actions } from '../OrbitL4ActionProvider';
import { OrbitL4Card } from '../actionCard';
import { orbitL4Status, setOrbitL4Status, useOrbitL4Status } from '../inFlightStore';
import L4CandidateList from './L4CandidateList';
import L4ConfirmCard from './L4ConfirmCard';
import L4OutOfScope from './L4OutOfScope';
import L4ResultBanner from './L4ResultBanner';
import L4StatusLine from './L4StatusLine';

const ExpiredText = styled.p`
  margin: 0;
  padding: 11px 13px;
  color: ${rsColor.secondary};
  ${responseType('body')}
`;

const PlainText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Expired: React.FC<{ message: string }> = ({ message }) => (
  <ResponsePanel data-testid="orbit-l4-expired">
    <ExpiredText>{message}</ExpiredText>
  </ResponsePanel>
);

/** Re-renders at `expiresAt`: the server never marks a proposal expired, the client clock does. */
const useExpiryClock = (expiresAt: number | null): void => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (expiresAt === null) {
      return undefined;
    }
    const wait = expiresAt - Date.now();
    if (wait <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setTick(tick => tick + 1), wait);
    return () => clearTimeout(timer);
  }, [expiresAt]);
};

interface IL4ActionReplyProps {
  turn: OrbitTurn;
  card: OrbitL4Card;
}

const L4ActionReply: React.FC<IL4ActionReplyProps> = ({ turn, card }) => {
  const { t } = useMultiLanguageTranslation();
  const { confirm, select, dismiss, openInBuilder } = useOrbitL4Actions();
  const { activeTurnId } = useOrbitContext();
  const status = useOrbitL4Status(turn.id);
  useExpiryClock(card.kind === 'confirm' ? card.expiresAt : null);

  // `mode: modal` opens the builder by itself, but only for the turn this tab is polling: a turn
  // loaded from history or after a reload keeps its card and button instead.
  const autoOpen =
    card.kind === 'confirm' &&
    card.mode === 'modal' &&
    turn.id === activeTurnId &&
    !status.autoOpened;
  useEffect(() => {
    if (autoOpen && card.kind === 'confirm') {
      setOrbitL4Status(turn.id, { ...orbitL4Status(turn.id), autoOpened: true });
      openInBuilder(turn, card.step, card.builder);
    }
  }, [autoOpen, card, openInBuilder, turn]);

  if (status.savedRecord !== undefined) {
    return (
      <L4ResultBanner
        tone="success"
        text={t(`${TOrbit}.layer4.savedInBuilder`, { id: status.savedRecord.id })}
        link={{ ...status.savedRecord, subtype: null }}
      />
    );
  }

  switch (card.kind) {
    case 'working':
      return <L4StatusLine step={card.step} />;
    case 'picker':
      return card.live ? (
        <L4CandidateList
          text={card.text}
          candidates={card.candidates}
          selectable={!status.busy}
          onSelect={candidate => select(turn, candidate)}
        />
      ) : (
        <PlainText data-testid="orbit-l4-text">{card.text}</PlainText>
      );
    case 'confirm': {
      if (status.expiredMessage !== undefined) {
        return <Expired message={status.expiredMessage ?? t(`${TOrbit}.layer4.expired.changed`)} />;
      }
      if (card.expiresAt !== null && card.expiresAt <= Date.now() && !status.busy) {
        return <Expired message={t(`${TOrbit}.layer4.expired.clock`)} />;
      }
      let mode: 'open' | 'working' | 'locked' | 'cancelled' = 'open';
      if (status.busy) {
        mode = status.builderOpen ? 'locked' : 'working';
      } else if (status.dismissed) {
        mode = 'cancelled';
      }
      return (
        <L4ConfirmCard
          text={card.text}
          step={card.step}
          expiresAt={card.expiresAt}
          mode={mode}
          failed={status.failed}
          buttons={card.buttons}
          onAction={actionId => {
            if (actionId === 'confirm') {
              confirm(turn);
            } else if (actionId === 'open_builder') {
              openInBuilder(turn, card.step, card.builder);
            } else {
              dismiss(turn);
            }
          }}
        />
      );
    }
    case 'confirmed':
      return (
        <L4ConfirmCard
          text={card.text}
          step={card.step}
          expiresAt={null}
          mode="confirmed"
          historyLabel={
            card.handedOffId === null
              ? undefined
              : t(`${TOrbit}.layer4.handedOff`, { id: card.handedOffId })
          }
        />
      );
    case 'expired':
      return <Expired message={t(`${TOrbit}.layer4.expired.${card.reason}`)} />;
    case 'result':
      return <L4ResultBanner tone={card.tone} text={card.text} link={card.link} />;
    case 'outOfScope':
      return <L4OutOfScope text={card.text} />;
    case 'text':
      return <PlainText data-testid="orbit-l4-text">{card.text}</PlainText>;
    default:
      return null;
  }
};

export default L4ActionReply;
